import React, { useState } from 'react';
import { X, Check, HelpCircle, Loader2, RefreshCw } from 'lucide-react';
import { Categoria } from '../../types/financeiro';
import { sanitizeText } from '../../utils/textSanitizer';

interface CategorizarModalProps {
  isOpen: boolean;
  onClose: () => void;
  transactions: any[];
  categories: Categoria[];
  onCategorize: (id: number, categoriaId: number) => Promise<void>;
}

function formatDateBR(dateStr?: string): string {
  if (!dateStr) return '-';
  const parts = dateStr.split('T')[0].split('-');
  if (parts.length === 3) {
    return `${parts[2]}/${parts[1]}/${parts[0]}`;
  }
  return dateStr;
}

export function CategorizarModal({ isOpen, onClose, transactions, categories, onCategorize }: CategorizarModalProps) {
  const [updatingId, setUpdatingId] = useState<number | null>(null);

  if (!isOpen) return null;

  const handleSelectCategory = async (transactionId: number, categoryId: number) => {
    try {
      setUpdatingId(transactionId);
      await onCategorize(transactionId, categoryId);
    } catch (error) {
      console.error('Erro ao categorizar transação:', error);
      alert('Não foi possível atualizar a categoria. Tente novamente.');
    } finally {
      setUpdatingId(null);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-xs flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl w-full max-w-3xl max-h-[85vh] shadow-2xl flex flex-col overflow-hidden animate-in zoom-in-95 duration-200">
        
        {/* Header */}
        <div className="bg-slate-900 text-white px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="p-2 bg-red-600 rounded-lg">
              <RefreshCw className="w-5 h-5 text-white animate-pulse" />
            </div>
            <div>
              <h3 className="font-black text-lg uppercase tracking-tight">Categorizar Lançamentos Pendentes</h3>
              <p className="text-xs text-slate-300">Defina uma categoria para os títulos classificados como "Sem Categoria".</p>
            </div>
          </div>
          <button 
            onClick={onClose}
            className="p-1.5 hover:bg-slate-800 rounded-lg text-slate-400 hover:text-white transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6 space-y-4">
          {transactions.length === 0 ? (
            <div className="text-center py-12 space-y-3">
              <div className="inline-flex p-4 bg-emerald-50 text-emerald-600 rounded-full">
                <Check className="w-8 h-8" />
              </div>
              <h4 className="text-sm font-bold text-slate-800">Tudo Organizado!</h4>
              <p className="text-xs text-slate-500 max-w-md mx-auto">
                Não há transações "Sem Categoria" no período selecionado. Excelente gestão de caixa!
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              <p className="text-xs text-slate-500 font-bold mb-2">
                Total pendente: {transactions.length} {transactions.length === 1 ? 'lançamento' : 'lançamentos'}
              </p>
              
              <div className="divide-y divide-slate-100 border border-slate-200 rounded-xl overflow-hidden bg-slate-50">
                {transactions.map(t => {
                  const rawId = parseInt(t.id.replace('real-', ''));
                  return (
                    <div key={t.id} className="p-4 bg-white hover:bg-slate-50 transition-colors flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                      <div className="space-y-1 min-w-0 flex-1">
                        <div className="flex items-center gap-2">
                          <span className={`text-[9px] font-black uppercase px-2 py-0.5 rounded-full ${
                            t.tipo === 'entrada' ? 'bg-emerald-50 text-emerald-700 border border-emerald-100' : 'bg-red-50 text-red-700 border border-red-100'
                          }`}>
                            {t.tipo === 'entrada' ? 'Entrada' : 'Saída'}
                          </span>
                          <span className="text-xs font-bold text-slate-400 font-mono">
                            Vencimento: {formatDateBR(t.dt_vencimento)}
                          </span>
                        </div>
                        <h4 className="text-sm font-bold text-slate-800 truncate" title={sanitizeText(t.descricao)}>
                          {sanitizeText(t.descricao)}
                        </h4>
                        {t.dt_pagamento && (
                          <p className="text-[10px] text-slate-400">
                            Quitado em: {formatDateBR(t.dt_pagamento)}
                          </p>
                        )}
                      </div>

                      <div className="flex items-center gap-4 flex-shrink-0 justify-between sm:justify-end">
                        <span className={`text-sm font-black ${t.tipo === 'entrada' ? 'text-emerald-600' : 'text-red-600'}`}>
                          {new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(t.valor)}
                        </span>

                        <div className="relative">
                          {updatingId === rawId ? (
                            <div className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold text-slate-500 bg-slate-100 rounded-lg">
                              <Loader2 className="w-3.5 h-3.5 animate-spin text-red-600" />
                              Salvando...
                            </div>
                          ) : (
                            <select
                              onChange={(e) => handleSelectCategory(rawId, Number(e.target.value))}
                              defaultValue=""
                              className="bg-slate-50 hover:bg-slate-100 border border-slate-200 text-slate-700 text-xs font-bold rounded-lg px-3 py-2 outline-none focus:border-red-500 focus:ring-1 focus:ring-red-500 transition-all cursor-pointer max-w-[180px]"
                            >
                              <option value="" disabled>Selecionar Categoria...</option>
                              {categories.map(cat => (
                                <option key={cat.id} value={cat.id}>
                                  {sanitizeText(cat.nome)}
                                </option>
                              ))}
                            </select>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="bg-slate-50 border-t border-slate-100 px-6 py-4 flex justify-end">
          <button
            onClick={onClose}
            className="px-5 py-2 text-xs font-bold text-slate-700 bg-white border border-slate-200 rounded-xl hover:bg-slate-100 transition-colors"
          >
            Fechar
          </button>
        </div>

      </div>
    </div>
  );
}
